<template>
    <div class="users-index-container animated fadeIn">
        <b-card class="users-card">
            <div class="users-header mb-4">
                <div>
                    <h4>{{ $t('users') }}</h4>
                    <p class="text-muted mb-0">{{ $t('manageUsersDescription') }}</p>
                </div>
                <div class="header-buttons">
                    <b-button variant="primary" to="/admin/iam/invite" class="mr-2">
                        <b-icon icon="plus-circle"></b-icon> {{ $t('addUser') }}
                    </b-button>
                    <b-button variant="outline-primary" to="/admin/iam/create">
                        <b-icon icon="plus-circle"></b-icon> {{ $t('createApiUser') }}
                    </b-button>
                </div>
            </div>

            <div class="tabs-container mb-4">
                <div class="tabs-wrapper">
                    <b-tabs v-model="activeTab" class="users-tabs">
                        <b-tab :title="$t('platformUsers')">
                            <b-form-input
                                v-model="searchPlatform"
                                :placeholder="$t('filterUsers')"
                                size="lg"
                                class="mb-4"
                            >
                                <template #prepend>
                                    <b-icon icon="search"></b-icon>
                                </template>
                            </b-form-input>

                            <div class="users-grid">
                                <div 
                                    v-for="user in filteredItems" 
                                    :key="user.uuid"
                                    class="user-card"
                                    @click="edit(user)"
                                >
                                    <div class="user-card-header">
                                        <div class="user-info">
                                            <h5 class="user-name">{{ user.user.name }}</h5>
                                            <p class="user-email">{{ user.user.email }}</p>
                                            <div class="user-roles" v-if="user.roles && user.roles.length > 0">
                                                <b-badge 
                                                    v-for="role in user.roles" 
                                                    :key="role.role.uuid"
                                                    variant="primary"
                                                    class="role-badge"
                                                >
                                                    {{ role.role.name }}
                                                </b-badge>
                                            </div>
                                        </div>
                                        <b-icon icon="chevron-right" class="user-arrow"></b-icon>
                                    </div>
                                </div>
                            </div>

                            <div v-if="filteredItems.length === 0" class="no-users">
                                <b-icon icon="inbox" font-scale="3" class="mb-3"></b-icon>
                                <h5>{{ $t('noUsersFound') }}</h5>
                                <p class="text-muted">{{ searchPlatform ? $t('tryDifferentSearch') : $t('inviteFirstUser') }}</p>
                            </div>
                        </b-tab>

                        <b-tab :title="$t('apiUsers')">
                            <b-form-input
                                v-model="searchAPI"
                                :placeholder="$t('filterApiUsers')"
                                size="lg"
                                class="mb-4"
                            >
                                <template #prepend>
                                    <b-icon icon="search"></b-icon>
                                </template>
                            </b-form-input>

                            <div class="users-grid">
                                <div 
                                    v-for="apiUser in filteredApiUsers" 
                                    :key="apiUser.uuid"
                                    class="user-card"
                                    @click="editApiUser(apiUser)"
                                >
                                    <div class="user-card-header">
                                        <div class="user-info">
                                            <h5 class="user-name">{{ apiUser.username }}</h5>
                                            <div class="user-roles" v-if="apiUser.roles && apiUser.roles.length > 0">
                                                <b-badge 
                                                    v-for="role in apiUser.roles" 
                                                    :key="role.role.uuid"
                                                    variant="primary"
                                                    class="role-badge"
                                                >
                                                    {{ role.role.name }}
                                                </b-badge>
                                            </div>
                                        </div>
                                        <b-icon icon="chevron-right" class="user-arrow"></b-icon>
                                    </div>
                                </div>
                            </div>

                            <div v-if="filteredApiUsers.length === 0" class="no-users">
                                <b-icon icon="inbox" font-scale="3" class="mb-3"></b-icon>
                                <h5>{{ $t('noApiUsersFound') }}</h5>
                                <p class="text-muted">{{ searchAPI ? $t('tryDifferentSearch') : $t('createFirstApiUser') }}</p>
                            </div>
                        </b-tab>

                        <b-tab :title="$t('pendingInvites')">
                            <b-form-input
                                v-model="searchInvites"
                                :placeholder="$t('filterInvites')"
                                size="lg"
                                class="mb-4"
                            >
                                <template #prepend>
                                    <b-icon icon="search"></b-icon>
                                </template>
                            </b-form-input>

                            <div class="users-grid">
                                <div 
                                    v-for="invite in filteredInvites" 
                                    :key="invite.uuid"
                                    class="user-card invite-card"
                                >
                                    <div class="user-card-header">
                                        <div class="user-info">
                                            <h5 class="user-name">{{ invite.email }}</h5>
                                            <div class="user-meta">
                                                <b-badge variant="warning" pill class="mr-2">
                                                    <b-icon icon="clock"></b-icon> {{ $t('pending') }}
                                                </b-badge>
                                                <b-badge v-if="getRoleName(invite.role_uuid)" variant="info" pill>
                                                    <b-icon icon="shield"></b-icon> {{ getRoleName(invite.role_uuid) }}
                                                </b-badge>
                                            </div>
                                            <p class="invite-date">{{ $t('invitedOn') }}: {{ formatDate(invite.created_at) }}</p>
                                        </div>
                                        <b-button
                                            variant="outline-danger"
                                            size="sm"
                                            @click="deleteInvite(invite)"
                                            class="delete-invite-btn"
                                        >
                                            <b-icon icon="trash"></b-icon>
                                        </b-button>
                                    </div>
                                </div>
                            </div>

                            <div v-if="filteredInvites.length === 0" class="no-users">
                                <b-icon icon="inbox" font-scale="3" class="mb-3"></b-icon>
                                <h5>{{ $t('noPendingInvites') }}</h5>
                                <p class="text-muted">{{ searchInvites ? $t('tryDifferentSearch') : $t('inviteUsersToGetStarted') }}</p>
                            </div>
                        </b-tab>
                    </b-tabs>
                </div>
            </div>
        </b-card>
    </div>
</template>

<script>
export default {
    name: "Users",
    props: {
        caption: {
            type: String,
            default: 'Users'
        }
    },
    methods: {
        edit(item) {
            this.$router.push({path:"/admin/iam/edit/" + item.uuid})
        },

        editApiUser(item) {
            this.$router.push({path:"/admin/iam/api/" + item.uuid})
        },

        async loadInvites() {
            try {
                const response = await this.$http.get('/v1/project/invites')
                this.invites = response.data
            } catch (error) {
                console.error('Failed to load invites:', error)
                this.$mainDialog.msgBoxOk(this.$t('failedToLoadInvites'))
            }
        },

        async deleteInvite(invite) {
            const confirmed = await this.$mainDialog.msgBoxConfirm(
                this.$t('confirmDeleteInvite', { email: invite.email }),
                {
                    title: this.$t('deleteInvite'),
                    okVariant: 'danger',
                    okTitle: this.$t('delete'),
                    cancelTitle: this.$t('cancel')
                }
            )

            if (confirmed) {
                try {
                    await this.$http.delete(`/v1/project/invite/${invite.uuid}`)
                    this.invites = this.invites.filter(i => i.uuid !== invite.uuid)
                    this.$bvToast.toast(this.$t('inviteDeleted'), {
                        title: this.$t('success'),
                        variant: 'success',
                        solid: true
                    })
                } catch (error) {
                    console.error('Failed to delete invite:', error)
                    this.$mainDialog.msgBoxOk(this.$t('failedToDeleteInvite'))
                }
            }
        },

        getRoleName(roleUuid) {
            const roles = this.$store.getters.getRoles
            const role = roles.find(r => r.uuid === roleUuid)
            return role ? role.name : this.$t('unknown')
        },

        formatDate(dateString) {
            if (!dateString) return '-'
            const date = new Date(dateString)
            return date.toLocaleDateString(this.$i18n.locale, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        }
    },
    data: function() {
        return {
            items: null,
            apiUsers: null,
            invites: null,
            searchPlatform: '',
            searchAPI: '',
            searchInvites: '',
            activeTab: 0
        }
    },
    computed: {
        filteredItems() {
            if (!this.items) return []
            if (!this.searchPlatform) return this.items
            
            const search = this.searchPlatform.toLowerCase()
            return this.items.filter(item => {
                const name = (item.user && item.user.name) ? item.user.name.toLowerCase() : ''
                const email = (item.user && item.user.email) ? item.user.email.toLowerCase() : ''
                const roles = item.roles ? item.roles.map(r => r.role.name.toLowerCase()).join(' ') : ''
                
                return name.includes(search) || 
                       email.includes(search) || 
                       roles.includes(search)
            })
        },
        filteredApiUsers() {
            if (!this.apiUsers) return []
            if (!this.searchAPI) return this.apiUsers
            
            const search = this.searchAPI.toLowerCase()
            return this.apiUsers.filter(item => {
                const username = item.username ? item.username.toLowerCase() : ''
                const roles = item.roles ? item.roles.map(r => r.role.name.toLowerCase()).join(' ') : ''
                
                return username.includes(search) || 
                       roles.includes(search)
            })
        },
        filteredInvites() {
            if (!this.invites) return []
            if (!this.searchInvites) return this.invites
            
            const search = this.searchInvites.toLowerCase()
            return this.invites.filter(invite => {
                const email = invite.email ? invite.email.toLowerCase() : ''
                const roleName = this.getRoleName(invite.role_uuid).toLowerCase()
                
                return email.includes(search) || roleName.includes(search)
            })
        }
    },
    mounted() {
        this.$store.dispatch("fetchRoles").catch(error => {
            if (error.response.status == 500) {
                this.$serverErrorModal(error)
                return
            }

            this.$mainDialog.msgBoxOk(error.response.data.message)
        })

        this.$project.users().then(resp => {
            this.items = resp.data
        })

        this.$project.apiUsers().then(resp => {
            this.apiUsers = resp.data
        })

        this.loadInvites()
    },

    beforeCreate() {
        if (!this.$can(["read base_users", "see base_users"])) {
            this.$router.go(-1)
        }
    }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/_custom.scss';

.users-index-container {
    max-width: 1400px;
    margin: 0 auto;
}

.users-card {
    .users-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        
        h4 {
            color: var(--text-primary);
            font-weight: 600;
            margin: 0 0 4px 0;
        }
        
        p {
            font-size: 14px;
        }
        
        .header-buttons {
            display: flex;
            gap: 12px;
        }
    }
}

.users-tabs {
    ::v-deep .nav-tabs {
        border-bottom: 2px solid $dark-border2;
        
        .nav-link {
            border: none;
            border-bottom: 3px solid transparent;
            color: var(--text-secondary);
            padding: 12px 20px;
            font-weight: 500;
            transition: all 0.2s;
            
            &:hover {
                color: var(--text-primary);
                border-bottom-color: rgba(102, 126, 234, 0.5);
            }
            
            &.active {
                background: transparent;
                color: $protokol-royal-active;
                border-bottom-color: $protokol-royal-active;
            }
        }
    }
}

.users-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
    margin-top: 20px;
}

.user-card {
    background: $dark-hard2-background;
    border: 1px solid $dark-border2;
    border-radius: 12px;
    padding: 20px;
    cursor: pointer;
    transition: all 0.2s;
    
    &.invite-card {
        cursor: default;
        
        &:hover {
            border-color: $dark-border2;
            box-shadow: none;
            transform: none;
        }
    }
    
    &:hover {
        border-color: $protokol-royal-active;
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
        transform: translateY(-2px);
        
        .user-arrow {
            transform: translateX(4px);
            color: $protokol-royal-active;
        }
    }
    
    .user-card-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        
        .user-info {
            flex: 1;
            
            .user-name {
                color: var(--text-primary);
                font-size: 18px;
                font-weight: 600;
                margin: 0 0 4px 0;
            }
            
            .user-email {
                color: var(--text-secondary);
                font-size: 14px;
                margin: 0 0 12px 0;
            }
            
            .user-meta {
                margin-bottom: 12px;
                
                .badge {
                    font-size: 12px;
                    font-weight: 500;
                    padding: 6px 12px;
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    background: var(--border-default);
                    color: rgba(255, 255, 255, 0.9);
                    border: 1px solid var(--border-default);
                }
            }
            
            .user-roles {
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                
                .role-badge {
                    font-size: 11px;
                    font-weight: 500;
                    padding: 4px 10px;
                    background: rgba(102, 126, 234, 0.2);
                    color: $protokol-royal-active;
                    border: 1px solid rgba(102, 126, 234, 0.3);
                }
            }
            
            .invite-date {
                color: var(--text-muted);
                font-size: 12px;
                margin: 8px 0 0 0;
            }
        }
        
        .delete-invite-btn {
            margin-top: 4px;
            border-radius: 8px;
            padding: 6px 12px;
            
            &:hover {
                background: rgba(220, 53, 69, 0.1);
                border-color: #dc3545;
            }
        }
        
        .user-arrow {
            color: rgba(255, 255, 255, 0.4);
            font-size: 20px;
            transition: all 0.2s;
            margin-top: 4px;
        }
    }
}

.no-users {
    text-align: center;
    padding: 60px 20px;
    
    svg {
        color: var(--text-muted);
    }
    
    h5 {
        color: var(--text-primary);
        margin-bottom: 8px;
    }
    
    p {
        font-size: 14px;
    }
}
</style>